import { PortalApiError, portalFetch, portalJson } from './client';

/** Reads the server's machine-readable `error` code (for example "current-password-incorrect"). */
const portalErrorFrom = async (response: Response): Promise<PortalApiError> => {
  let code = `HTTP ${response.status}`;
  try {
    const data = (await response.json()) as { error?: string };
    if (data.error) code = data.error;
  } catch {
    // Not JSON; keep the status-based code.
  }
  return new PortalApiError(response.status, code);
};

export interface PortalProfile {
  customerId: number;
  username: string;
  displayName: string;
  phone: string;
  secondaryPhone: string;
  address: string;
  age: number | null;
  email: string;
}

export interface PortalTest {
  testCode: number;
  name: string;
  children: PortalTest[];
}

export interface PortalVisit {
  visitId: string;
  daySerial: number;
  visitDate: string;
  total: number;
  discountValue: number;
  paid: number;
  remaining: number;
  tests: PortalTest[];
  doctorName: string;
  resultSentOnWhatsApp: boolean;
  resultUrl: string | null;
}

interface WireProfile {
  CustomerId: number;
  Username: string;
  DisplayName: string;
  Phone?: string;
  SecondaryPhone?: string;
  Address?: string;
  Age?: number | null;
  Email?: string;
}

interface WireTest {
  TestCode: number;
  Name: string;
  Children: WireTest[];
}

interface WireVisit {
  VisitId: string;
  DaySerial: number;
  VisitDate: string;
  Total: number;
  DiscountValue: number;
  Paid: number;
  Remaining: number;
  Tests: WireTest[];
  DoctorName?: string;
  ResultSentOnWhatsApp?: boolean;
  ResultUrl?: string | null;
}

interface WireLoginResponse {
  IsSuccess: boolean;
  Token: string | null;
}

const mapTest = (test: WireTest): PortalTest => ({
  testCode: test.TestCode,
  name: test.Name,
  children: (test.Children ?? []).map(mapTest),
});

const mapVisit = (visit: WireVisit): PortalVisit => ({
  visitId: visit.VisitId,
  daySerial: visit.DaySerial,
  visitDate: visit.VisitDate,
  total: visit.Total,
  discountValue: visit.DiscountValue,
  paid: visit.Paid,
  remaining: visit.Remaining,
  tests: (visit.Tests ?? []).map(mapTest),
  doctorName: visit.DoctorName ?? '',
  resultSentOnWhatsApp: visit.ResultSentOnWhatsApp ?? false,
  resultUrl: visit.ResultUrl ?? null,
});

/** Lab runtime settings for the device must be loaded before login; failures are not fatal. */
export const bootstrapLabDevice = async (): Promise<void> => {
  try {
    await portalFetch('/api/Config/bootstrap?deviceNumber=1');
  } catch {
    // Login still runs; the server reports a clear error if the lab is unreachable.
  }
};

export type PortalRole = 'customer' | 'doctor';

const ROLE_LOGIN_TYPE: Record<PortalRole, 'Customer' | 'Doctor'> = { customer: 'Customer', doctor: 'Doctor' };

interface LoginInput {
  username: string;
  password: string;
  lang: 'ar' | 'en';
}

/** The server decides the account type from the credentials; a mismatched `type` is answered with a failed login. */
const authenticateAs = async (role: PortalRole, input: LoginInput): Promise<string> => {
  const response = await portalJson<WireLoginResponse>('/Secuirty/AuthenticateWithType', {
    method: 'POST',
    body: {
      username: input.username.trim(),
      password: input.password,
      type: ROLE_LOGIN_TYPE[role],
      lang: input.lang,
      IsDesktop: false,
      addons: false,
      ClientDateTime: new Date().toISOString(),
    },
  });

  if (!response.IsSuccess || !response.Token) {
    throw new PortalApiError(401, 'invalid-credentials');
  }

  return response.Token;
};

/** Verifies a doctor's password with a fresh login; the returned token is discarded. */
export const authenticateDoctor = async (input: LoginInput): Promise<void> => {
  await authenticateAs('doctor', input);
};

export const loginCustomer = async (input: LoginInput): Promise<string> => {
  await bootstrapLabDevice();
  return authenticateAs('customer', input);
};

/** One login for everyone: customers first (the common case), then doctors. Only a 401 falls through to the next type. */
export const loginAccount = async (input: LoginInput): Promise<{ token: string; role: PortalRole }> => {
  await bootstrapLabDevice();

  try {
    return { token: await authenticateAs('customer', input), role: 'customer' };
  } catch (error) {
    if (!(error instanceof PortalApiError && error.status === 401)) throw error;
  }

  return { token: await authenticateAs('doctor', input), role: 'doctor' };
};

export const fetchProfile = async (token: string): Promise<PortalProfile> => {
  const profile = await portalJson<WireProfile>('/api/customer-portal/me', { token });
  return {
    customerId: profile.CustomerId,
    username: profile.Username,
    displayName: profile.DisplayName,
    phone: profile.Phone ?? '',
    secondaryPhone: profile.SecondaryPhone ?? '',
    address: profile.Address ?? '',
    age: profile.Age ?? null,
    email: profile.Email ?? '',
  };
};

export const fetchVisits = async (token: string): Promise<PortalVisit[]> => {
  const visits = await portalJson<WireVisit[]>('/api/customer-portal/me/visits', { token });
  return visits.map(mapVisit);
};

export interface PortalPointMovement {
  id: number;
  /** "earned" comes from a paid visit; "used" is points the cashier redeemed. */
  kind: 'earned' | 'used';
  points: number;
  date: string | null;
  visitId: string | null;
  daySerial: number | null;
}

export interface PortalPoints {
  earned: number;
  used: number;
  remaining: number;
  movements: PortalPointMovement[];
}

// The lab API's casing is not guaranteed for this endpoint, so every field is read case-insensitively.
type LooseRecord = Record<string, unknown>;

const pick = (record: LooseRecord, name: string): unknown => {
  const key = Object.keys(record).find((candidate) => candidate.toLowerCase() === name.toLowerCase());
  return key === undefined ? undefined : record[key];
};

const num = (value: unknown): number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Reads the existing lab endpoint; the customer code is the account id (same rule the profile uses). */
export const fetchPoints = async (token: string, customerId: number): Promise<PortalPoints> => {
  const body = await portalJson<LooseRecord>('/CustomerCasherPoints/GetCustomerPointsHistory', {
    method: 'POST',
    token,
    body: { customerCode: String(customerId) },
  });

  const items = Array.isArray(pick(body, 'items')) ? (pick(body, 'items') as LooseRecord[]) : [];
  const movements = items
    .map((item): PortalPointMovement => {
      const visitId = pick(item, 'visitId');
      const daySerial = pick(item, 'daySerial');
      const createdAt = pick(item, 'createdAt');
      return {
        id: num(pick(item, 'id')),
        kind: num(pick(item, 'transactionType')) === 1 ? 'earned' : 'used',
        points: num(pick(item, 'points')),
        date: typeof createdAt === 'string' && createdAt ? createdAt : null,
        visitId: visitId === null || visitId === undefined ? null : String(visitId),
        daySerial: daySerial === null || daySerial === undefined ? null : num(daySerial),
      };
    })
    .filter((movement) => movement.points !== 0);

  return {
    earned: num(pick(body, 'earnedPoints')),
    used: num(pick(body, 'usedPoints')),
    remaining: num(pick(body, 'remainingPoints')),
    movements,
  };
};

export const RESULT_LINK_PREFIX = 'https://r.elhamylabs.com/';

export const resultLinkFor = (visitId: string): string => `${RESULT_LINK_PREFIX}r/${encodeURIComponent(visitId)}`;

/**
 * Hidden check: does a PDF exist on r.elhamylabs.com for this visit? Only a definite answer (404 or not a PDF) is "no";
 * a network failure counts as "yes" so a blip does not hide a real result. The body is cancelled, only headers are read.
 */
export const resultExistsOnLink = async (visitId: string): Promise<boolean> => {
  try {
    const response = await fetch(resultLinkFor(visitId));
    const isPdf = (response.headers.get('content-type') ?? '').toLowerCase().includes('application/pdf');
    void response.body?.cancel();
    return response.ok && isPdf;
  } catch {
    return true;
  }
};

/** Fetches the PDF straight from r.elhamylabs.com so the browser can save it. Null means "use the server download instead". */
export const fetchResultFromLink = async (resultUrl: string): Promise<Blob | null> => {
  if (!resultUrl.startsWith(RESULT_LINK_PREFIX)) return null;

  try {
    const response = await fetch(resultUrl);
    if (!response.ok) return null;
    if (!(response.headers.get('content-type') ?? '').toLowerCase().includes('application/pdf')) return null;
    return await response.blob();
  } catch {
    return null;
  }
};

export interface ResultRequestStatus {
  isPending: boolean;
  isLocked: boolean;
}

/** Why a result request was refused; "failed" covers everything unexpected. */
export type ResultRequestError = 'already-requested' | 'payment-required' | 'not-found' | 'failed';

interface WireResultRequestStatus {
  IsPending?: boolean;
  isPending?: boolean;
  IsLocked?: boolean;
  isLocked?: boolean;
}

export const mapResultRequestStatus = (wire: WireResultRequestStatus): ResultRequestStatus => ({
  isPending: Boolean(wire.IsPending ?? wire.isPending),
  isLocked: Boolean(wire.IsLocked ?? wire.isLocked),
});

export const resultRequestErrorFor = (status: number): ResultRequestError => {
  if (status === 409) return 'already-requested';
  if (status === 402) return 'payment-required';
  if (status === 404) return 'not-found';
  return 'failed';
};

export const fetchResultRequestStatus = async (token: string, visitId: string): Promise<ResultRequestStatus> =>
  mapResultRequestStatus(
    await portalJson<WireResultRequestStatus>(
      `/api/customer-portal/me/visits/${encodeURIComponent(visitId)}/result-request`,
      { token },
    ),
  );

/** Source 1 = website. The server checks the visit belongs to the signed-in customer and has no unpaid balance. */
export const requestVisitResult = async (token: string, visitId: string): Promise<void> => {
  const response = await portalFetch(`/api/customer-portal/me/visits/${encodeURIComponent(visitId)}/result-request`, {
    method: 'POST',
    token,
    body: { Source: 1 },
  });
  if (!response.ok) throw new PortalApiError(response.status, resultRequestErrorFor(response.status));
};

export interface PortalComplaint {
  complaintId: number;
  body: string;
  createdDate: string;
  isHandled: boolean;
}

interface WireComplaint {
  ComplaintId: number;
  Body: string;
  CreatedDate: string;
  IsHandled: boolean;
}

export const fetchComplaints = async (token: string): Promise<PortalComplaint[]> => {
  const complaints = await portalJson<WireComplaint[]>('/api/customer-portal/me/complaints', { token });
  return complaints.map((complaint) => ({
    complaintId: complaint.ComplaintId,
    body: complaint.Body,
    createdDate: complaint.CreatedDate,
    isHandled: complaint.IsHandled,
  }));
};

export const submitComplaint = async (
  token: string,
  input: { email: string; phone: string; body: string },
): Promise<void> => {
  const response = await portalFetch('/api/customer-portal/me/complaints', {
    method: 'POST',
    token,
    body: { Email: input.email.trim(), Phone: input.phone.trim(), Body: input.body.trim() },
  });
  if (!response.ok) {
    throw await portalErrorFrom(response);
  }
};

export const changeCustomerPassword = async (
  token: string,
  input: { currentPassword: string; newPassword: string },
): Promise<void> => {
  const response = await portalFetch('/api/customer-portal/me/password', {
    method: 'POST',
    token,
    body: { CurrentPassword: input.currentPassword, NewPassword: input.newPassword },
  });
  if (!response.ok) {
    throw await portalErrorFrom(response);
  }
};

export const deleteCustomerAccount = async (token: string, password: string): Promise<void> => {
  const response = await portalFetch('/api/customer-portal/me/account/delete', {
    method: 'POST',
    token,
    body: { Password: password },
  });
  if (!response.ok) {
    throw await portalErrorFrom(response);
  }
};
